(() => {
  const SWIPE_THRESHOLD = 28;
  const MOBILE_BREAKPOINT = 600;
  const MOBILE_VIEW_STORAGE_KEY = 'loomformaCollectionMobileColumns';
  const DESKTOP_VIEW_STORAGE_KEY = 'loomformaCollectionDesktopColumns';
  const VIEW_ANIMATION_DURATION = 420;

  const initCollectionFilters = (scope = document) => {
    const forms = scope.querySelectorAll('[data-loomforma-filter-form]');

    forms.forEach((form) => {
      if (form.dataset.filtersReady === 'true') return;
      form.dataset.filtersReady = 'true';

      form.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (!target.matches('[data-filter-auto-submit], select[data-filter-auto-submit]')) return;
        form.requestSubmit();
      });

      document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (form.contains(target)) return;

        form.querySelectorAll('.loomforma-filter-group[open]').forEach((group) => {
          group.removeAttribute('open');
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        form.querySelectorAll('.loomforma-filter-group[open]').forEach((group) => {
          group.removeAttribute('open');
        });
      });
    });
  };

  const initCardGallery = (scope = document) => {
    const galleries = scope.querySelectorAll('[data-card-gallery]');

    galleries.forEach((gallery) => {
      if (gallery.dataset.galleryReady === 'true') return;
      gallery.dataset.galleryReady = 'true';

      const card = gallery.closest('.loomforma-product-card');
      const link = gallery.closest('[data-card-link]');

      if (!card || !link) return;

      let startX = 0;
      let startY = 0;
      let isTracking = false;
      let suppressClick = false;

      const showSecondary = (show) => {
        card.classList.toggle('is-secondary-visible', show);
      };

      gallery.addEventListener(
        'touchstart',
        (event) => {
          if (!event.touches[0]) return;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          isTracking = true;
        },
        { passive: true }
      );

      gallery.addEventListener(
        'touchend',
        (event) => {
          if (!isTracking || !event.changedTouches[0]) return;
          isTracking = false;

          const deltaX = event.changedTouches[0].clientX - startX;
          const deltaY = event.changedTouches[0].clientY - startY;

          if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;

          showSecondary(deltaX < 0);
          suppressClick = true;

          window.setTimeout(() => {
            suppressClick = false;
          }, 320);
        },
        { passive: true }
      );

      gallery.addEventListener('touchcancel', () => {
        isTracking = false;
      });

      link.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        suppressClick = false;
      });
    });
  };

  const initCollectionViewToggle = (scope = document) => {
    const grid = scope.querySelector('[data-loomforma-catalog-grid]');
    const toggle = scope.querySelector('[data-loomforma-mobile-view-toggle]');

    if (!grid || !toggle || toggle.dataset.viewReady === 'true') return;

    const buttons = toggle.querySelectorAll('[data-loomforma-mobile-columns]');
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeColumns = null;
    let isAnimating = false;
    let resizeFrame = null;

    const getDesktopSingleCardWidth = () => {
      const firstItem = grid.querySelector('.loomforma-catalog-item');
      const firstCard = firstItem?.querySelector('.loomforma-product-card');
      const firstImage = firstItem?.querySelector('.loomforma-product-image-container');

      if (!firstItem || !firstCard || !firstImage) {
        return Math.min(grid.clientWidth, 680);
      }

      const gridRect = grid.getBoundingClientRect();
      const cardRect = firstCard.getBoundingClientRect();
      const imageRect = firstImage.getBoundingClientRect();
      const fixedCardHeight = Math.max(0, cardRect.height - imageRect.height);
      const imageRatio = imageRect.width > 0 ? imageRect.height / imageRect.width : 4 / 3;
      const availableHeight = Math.max(360, window.innerHeight - Math.max(gridRect.top, 0) - 24);
      const widthFromHeight = Math.max(260, (availableHeight - fixedCardHeight) / imageRatio);

      return Math.max(260, Math.min(grid.clientWidth, 680, widthFromHeight));
    };

    const syncView = (columns) => {
      const isMobile = mediaQuery.matches;
      const isSingle = columns === '1';

      grid.classList.toggle('is-mobile-single', isMobile && isSingle);
      if (!isMobile && isSingle) {
        grid.style.gridTemplateColumns = `minmax(0, ${getDesktopSingleCardWidth()}px)`;
        grid.style.justifyContent = 'center';
      } else {
        grid.style.gridTemplateColumns = '';
        grid.style.justifyContent = '';
      }
      grid.style.rowGap = !isMobile && isSingle ? '40px' : '';

      buttons.forEach((button) => {
        const isActive = button.getAttribute('data-loomforma-mobile-columns') === columns;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      activeColumns = columns;
    };

    const animateViewChange = (columns) => {
      if (prefersReducedMotion.matches || isAnimating) {
        syncView(columns);
        return;
      }

      const items = Array.from(grid.querySelectorAll('.loomforma-catalog-item'));

      if (!items.length) {
        syncView(columns);
        return;
      }

      const firstRects = new Map(items.map((item) => [item, item.getBoundingClientRect()]));

      syncView(columns);

      const lastRects = new Map(items.map((item) => [item, item.getBoundingClientRect()]));
      const animations = [];

      items.forEach((item) => {
        const firstRect = firstRects.get(item);
        const lastRect = lastRects.get(item);

        if (!firstRect || !lastRect || !lastRect.width || !lastRect.height) return;

        const deltaX = firstRect.left - lastRect.left;
        const deltaY = firstRect.top - lastRect.top;
        const scaleX = firstRect.width / lastRect.width;
        const scaleY = firstRect.height / lastRect.height;

        const hasMovement =
          Math.abs(deltaX) > 0.5 ||
          Math.abs(deltaY) > 0.5 ||
          Math.abs(scaleX - 1) > 0.01 ||
          Math.abs(scaleY - 1) > 0.01;

        if (!hasMovement) return;

        const animation = item.animate(
          [
            {
              transform: `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`,
              transformOrigin: 'top left',
              opacity: 0.92
            },
            {
              transform: 'translate(0, 0) scale(1, 1)',
              transformOrigin: 'top left',
              opacity: 1
            }
          ],
          {
            duration: VIEW_ANIMATION_DURATION,
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            fill: 'both'
          }
        );

        animations.push(animation);
      });

      if (!animations.length) return;

      isAnimating = true;
      grid.classList.add('is-view-transitioning');

      Promise.allSettled(animations.map((animation) => animation.finished)).finally(() => {
        grid.classList.remove('is-view-transitioning');
        isAnimating = false;
      });
    };

    const getStorageKey = () => (mediaQuery.matches ? MOBILE_VIEW_STORAGE_KEY : DESKTOP_VIEW_STORAGE_KEY);

    const applyStoredView = () => {
      const storedColumns = window.localStorage.getItem(getStorageKey()) || '2';
      syncView(storedColumns);
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const columns = button.getAttribute('data-loomforma-mobile-columns') || '2';
        if (columns === activeColumns || isAnimating) return;
        window.localStorage.setItem(getStorageKey(), columns);
        animateViewChange(columns);
      });
    });

    window.addEventListener(
      'resize',
      () => {
        if (activeColumns == null || isAnimating) return;
        if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
          syncView(activeColumns);
          resizeFrame = null;
        });
      },
      { passive: true }
    );

    mediaQuery.addEventListener('change', applyStoredView);
    applyStoredView();
    toggle.dataset.viewReady = 'true';
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCollectionFilters();
      initCardGallery();
      initCollectionViewToggle();
    });
  } else {
    initCollectionFilters();
    initCardGallery();
    initCollectionViewToggle();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initCollectionFilters(event.target);
    initCardGallery(event.target);
    initCollectionViewToggle(event.target);
  });
})();
